import { Chip, type ChipProps } from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { AnnouncementSeverity } from '~/generated/graphql';

// Chip colours and Alert severities happen to share these names.
export const SEVERITY_COLOR = {
  CRITICAL: 'error',
  INFO: 'info',
  WARN: 'warning',
} as const satisfies Record<AnnouncementSeverity, 'error' | 'info' | 'warning'>;

// The enum name is not the column value the API counts under, so the mapping is written down
// rather than assumed from the casing.
export const SEVERITY_FILTER = {
  CRITICAL: 'critical',
  INFO: 'info',
  WARN: 'warn',
} as const satisfies Record<AnnouncementSeverity, string>;

// Spelled out rather than built from the severity, so i18n-tasks can see the keys are used.
export const useSeverityLabels = (): Record<AnnouncementSeverity, string> => {
  const { t } = useTranslation();

  return {
    CRITICAL: t('components.announcements.severity.critical'),
    WARN: t('components.announcements.severity.warn'),
    INFO: t('components.announcements.severity.info'),
  };
};

export interface SeverityChipProps extends Omit<ChipProps, 'color' | 'label' | 'size'> {
  severity: AnnouncementSeverity;
  count?: number;
}

export const SeverityChip = ({ severity, count, ...props }: SeverityChipProps) => {
  const label = useSeverityLabels()[severity];

  return (
    <Chip
      {...props}
      size="small"
      color={SEVERITY_COLOR[severity]}
      label={count === undefined ? label : `${count} ${label}`}
    />
  );
};
